import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { environment } from '../../environments/environment';
import Analytics from 'analytics';
import googleAnalytics from '@analytics/google-analytics';
import { LoadingController, Platform, ToastController } from '@ionic/angular';
const pluralize = require('pluralize');

@Injectable({
  providedIn: 'root',
})
export class PouchService {
  apiUrl: string = environment.api;
  database: string = '';
  database_name: string = '';
  customer_user = null;
  translations;
  untranslated = [];
  loading;
  language = 'en';
  languages: any[] = [
    { code: 'en', name: 'English' },
    { code: 'zh', name: '中文 (zhōngwén)' },
    { code: 'hi', name: 'हिन्दी (hindī)' },
    { code: 'es', name: 'español [espaˈɲol]' },
    { code: 'fr', name: 'français [fʁɑ̃sɛ]' },
    { code: 'ar', name: '(al arabiya) العربية' },
    { code: 'ru', name: 'Русский язык (Russkij jazyk)' },
    { code: 'pt', name: 'português' },
    { code: 'pl', name: 'polski' },
    { code: 'nl', name: 'Nederlands' },
    { code: 'ja', name: '日本語 (nihongo)' },
    { code: 'it', name: 'italiano' },
    { code: 'tr', name: 'Türkçe' },
    { code: 'ko', name: '한국어 [韓國語] (han-guk-eo)' },
    { code: 'de', name: 'Deutsch [dɔʏtʃ]' },
  ];
  health_systems = ['patient', 'pharmacy', 'hospital', 'ward', 'bed', 'prescription', 'laboratory', 'radiology'];
  appinfo = {
    product: 'patient',
    title: 'Perfect Customer Management',
    logo: 'assets/img/login.webp',
  };
  gsetting: any = {
    id: '',
    startdate: null,
    expiredate: null,
  };
  credentials = {
    user_id: '',
  };
  user: any = {
    id: '',
    usertype: 'customer',
    fullname: '',
  };
  analytics = Analytics({
    app: 'sarutech.com',
    plugins: [
      googleAnalytics({
        measurementIds: [environment.analytics],
      }),
    ],
  });
  headers;
  lease: any = null;

  constructor(private http: HttpClient, public loadingCtrl: LoadingController, public toastController: ToastController) {
    this.headers = new HttpHeaders().append('Content-Type', 'application/json').append('Access-Control-Allow-Origin', '*');
  }

  async setdatabase(database_name: string): Promise<any> {
    let response: any = await this.http.post(`${this.apiUrl}/app/rest`, { type: 'checkuser', username: database_name }).toPromise();
    if (response.status == 'error') {
      this.database = `${response.customer.data.product}$${database_name}`;
      this.database_name = database_name;
      localStorage.setItem('customer_database', this.database);
      localStorage.setItem('customer_database_name', this.database_name);
      this.get(database_name, 'setting').then((res) => {
        this.gsetting = res;
      });
      return true;
    } else {
      return false;
    }
  }

  async update(data, type): Promise<any> {
    const doc: any = {
      _id: `${type}_2_${data.id}`,
      data: { ...data },
      _attachments: data?.attachments || undefined,
    };

    // normalise revs
    if (data.rev) doc._rev = data.rev;

    // remove app-level ids/revs from the stored `data` section
    delete doc.data.id;
    delete doc.data.rev;
    delete doc.data.attachments; // ensure no duplicate
    delete doc.data._attachments;

    const res: any = await this.http
      .post(`${this.apiUrl}/app/update`, {
        doc,
        database: this.database,
      })
      .toPromise();
    this.sendEntityNotificationEmail(data, type);
    return { ...data, rev: res?.rev };
  }

  sendEntityNotificationEmail(data: any, type: string) {
    if (!this.customer_user?.email) return;
    const customerName = this.customer_user?.name || 'Customer';
    const action = data?.rev ? 'Updated' : 'Added';
    const itemType = this.rename(type);
    const summary = data?.description || data?.reason || data?.title || data?.name || 'No additional details were provided';
    const subject = `${customerName} – ${action} ${itemType}`;
    const url = `https://${this.appinfo.product}.sarutech.com/${this.gsetting.username}`;
    const message = `
        Hello,<br><br>
        
        <strong>${customerName}</strong> has ${action.toLowerCase()} the following ${itemType}:<br><br>
        
        <em>${summary}</em><br><br>
        
        Please log in to your account to view the full details:<br>
        <a href="${url}">${url}</a><br><br>
        
        Thank you,<br>
        ${this.appinfo.title}
    `;
    this.email(this.gsetting.email, subject, message);
  }

  async get(id: string, type, database = this.database || localStorage.getItem('customer_database')): Promise<any> {
    if (id && database) {
      try {
        let response: any = await this.http.post(`${this.apiUrl}/app/get`, { id: `${type}_2_${id}`, database: database }).toPromise();
        return {
          ...response.data,
          id: response._id.split('_2_')[1],
          rev: response._rev,
          attachments: response?._attachments,
        };
      } catch (error) {
        console.log(`Cannot find ${type} with id ${id}`);
        return null;
      }
    }
  }

  async getAll(type): Promise<any> {
    if (!type) {
      return [];
    }
    return this.http
      .post(`${this.apiUrl}/app/getAll`, { entity: type, database: this.database })
      .toPromise()
      .then((responses: any) => {
        return responses
          .map((response) => {
            return {
              ...response.doc.data,
              id: response.doc._id.split('_2_')[1],
              rev: response.doc._rev,
            };
          })
          .reverse();
      });
  }

  getAllRx(type): Observable<any> {
    return from(this.getAll(type));
  }

  getAttachment(type: string, id: string, attachmentId: string, content_type = 'image/png', database = this.database): Promise<Blob> {
    if (id.includes('_2_')) {
      id = id.split('_2_')[1];
    }
    return this.http
      .get(`${this.apiUrl}/app/getAttachment`, {
        responseType: 'blob',
        params: {
          id: `${type}_2_${id}`,
          filename: attachmentId,
          database: database,
          content_type: content_type,
        },
      })
      .toPromise()
      .catch((error) => {
        console.error('Error fetching attachment: ', error);
        throw error;
      });
  }

  urltoFile(url, filename, mimeType) {
    return fetch(url)
      .then(function (res) {
        return res.arrayBuffer();
      })
      .then(function (buf) {
        return new File([buf], filename, { type: mimeType });
      });
  }

  rename(name: any, language: string = this.language, type: string = 'input', item: string | null = null): string {
    if (name === undefined || name === null) return '';
    /* -------------------------------------------------
     * 1. Capture original casing
     * ------------------------------------------------- */
    const original = String(name);

    const detectCase = (v: string): 'lower' | 'upper' | 'title' => {
      if (type === 'title') return 'title';
      if (v === v.toLowerCase()) return 'lower';
      if (v === v.toUpperCase()) return 'upper';
      return 'title';
    };

    const originalCase = detectCase(original);

    const restoreCase = (v: string): string => {
      switch (originalCase) {
        case 'upper':
          return v.toUpperCase();
        case 'lower':
          return v.toLowerCase();
        case 'title':
          return this.titleCase(v, original);
        default:
          return this.titleCase(v, original);
      }
    };

    let value = original;

    /* -------------------------------------------------
     * 2. Special case: drug dictionary
     * ------------------------------------------------- */
    if (type === 'drug_dictionary' && item) {
      const cleanItem = item.replace(/_/g, ' ');
      value = value.replace(new RegExp(cleanItem, 'i'), '');
      value = value.replace(/\. /g, '.<br><br>');
      return value;
    }

    /* -------------------------------------------------
     * 3. Type-specific transforms
     * ------------------------------------------------- */
    if (type === 'report') {
      value = value.replace('_name', '');
    }

    if (type === 'date') {
      return new Date(value).toLocaleString().slice(0, -3);
    }

    if (type === 'number') {
      return Number(value).toLocaleString();
    }

    if (type === '@') {
      value = value.substring(1);
    }

    /* -------------------------------------------------
     * 4. Domain / product-specific renames
     * ------------------------------------------------- */
    const productMap: Record<string, Record<string, string>> = {
      product: { amount: 'selling_price', cost: 'cost_price' },
      Store: { amount: 'selling_price', cost: 'cost_price' },
    };

    value = productMap[type]?.[value] ?? value;

    value = value.replace('radiology', 'radiology ').replace('laboratory', 'laboratory ').replace('zoom', 'zoom ');

    if (this.health_systems.includes(this.appinfo.product)) {
      value = value.replace('customer', 'patient');
    } else if (this.appinfo.product === 'property' || type === 'property') {
      value = value.toLowerCase().replace('customer', 'tenant');
    } else if (this.appinfo.product === 'tailor') {
      value = value.replace('form', 'measurement');
    }

    /* -------------------------------------------------
     * 5. Pluralisation rules
     * ------------------------------------------------- */
    if ((type === 'array' || type === 'form') && !value.endsWith('s') && !value.includes('_')) {
      value = pluralize(value);
    }

    if (type === 's') {
      value = pluralize(value);
    }

    /* -------------------------------------------------
     * 6. Normalise before translation
     * ------------------------------------------------- */
    value = value.replace(/_/g, ' ').trim();
    const lookupKey = value.toLowerCase();

    /* -------------------------------------------------
     * 7. Translation lookup
     * ------------------------------------------------- */
    if (language !== 'en' && this.translations) {
      const translated = this.translations[lookupKey]?.[language] ?? this.translations[value]?.[language];

      if (translated) {
        value = translated;
      } else if (!this.untranslated.includes(lookupKey)) {
        this.untranslated.push(lookupKey);
      }
    }

    /* -------------------------------------------------
     * 8. Restore original casing
     * ------------------------------------------------- */
    return restoreCase(value);
  }

  getTranslations() {
    this.getJSON('./assets/data/translations.json').subscribe((data) => {
      this.translations = data;
    });
  }

  titleCase(text: any, original: any = null): string {
    if (text == null) return '';
    const words = String(text).trim().toLowerCase().split(/\s+/),
      originalWords = original ? String(original).trim().split(/\s+/) : [];
    if (!words.length) return '';
    const isAllCaps = (w: string) => w && w === w.toUpperCase() && /[A-Z]/.test(w);
    return (words.length <= 4 ? words.map((w, i) => (isAllCaps(originalWords[i]) ? originalWords[i] : w.charAt(0).toUpperCase() + w.slice(1))) : words.map((w, i) => (isAllCaps(originalWords[i]) ? originalWords[i] : i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))).join(' ');
  }

  isArray(str) {
    return Array.isArray(str);
  }

  getJSON(url): Observable<any> {
    return this.http.get(url);
  }

  selectlanguage(language) {
    //when user has not logged in or created a database
    this.language = language;
    if (this.translations == undefined) {
      this.getTranslations();
    }
  }

  updateTranslations() {}

  saveTranslations() {
    this.getJSON('./assets/data/translations.json').subscribe((data) => {
      this.get('translations', undefined).then((res) => {
        res.data = data;
        this.update(res, undefined).then((as) => {});
      });
    });
  }

  async email(email: string, subject = '', body = '', name = '') {
    // validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      this.presentToast('Invalid email address', 'toast-warning');
      return;
    }

    // ===============================
    // USE EMAIL PROVIDER (API)
    // ===============================
    if (this.gsetting?.email_provider?.api_key) {
      const data = {
        to: email, // single email only
        subject: subject,
        message: body,
        ...this.gsetting.email_provider,
        from: this.gsetting.email_provider?.from || this.gsetting.email,
      };

      this.http.post('https://sarutech.com/api/app/send_email', data).subscribe(
        async (res: any) => {
          this.presentToast(res?.status || 'Email sent', 'toast-success');
        },
        async (err) => {
          this.presentToast(err?.error?.status || 'Failed to send email', 'toast-danger');
        }
      );
      return;
    }
  }

  sendEmailToUser(email, subject, message) {
    //sarutech email
    let data = {
      email: email,
      subject: subject,
      message: message,
    };
    this.http.post('https://sarutech.com/api/app/mail', data).subscribe((res) => {
      this.presentToast('Email sent');
    });
  }

  async presentToast(message, type = 'toast-success') {
    const toast = await this.toastController.create({
      message: message,
      duration: 4000,
      cssClass: type,
    });
    toast.present();
  }

  localDate(type = 'datetime') {
    let date = new Date();
    let offsetMs = date.getTimezoneOffset() * 60 * 1000;
    let msLocal = date.getTime() - offsetMs;
    let dateLocal = new Date(msLocal);
    let iso = dateLocal.toISOString();
    let isoLocal;
    if (type == 'date') {
      //diffrent for date and datetime.
      isoLocal = iso.slice(0, 10);
    } else {
      isoLocal = iso.slice(0, 19);
    }

    return isoLocal;
  }

  print(options: any = { name: 'Document', duplex: true }) {
    if (typeof document.execCommand === 'function') {
      const success = document.execCommand('print', false, null);
      if (!success) {
        window.print();
      }
    } else {
      window.print();
    }
  }

  convertToNumber(event): number {
    return +event;
  } //workaround for number input returning string
}
